// File: components/StartupCalculator.js
import React, {useState, useEffect} from 'react';
import LoginScreen from './LoginScreen';
import BaseConfiguration from './BaseConfiguration';
import CoreTeamSalaries from './CoreTeamSalaries';
import WorkEnvironment from './WorkEnvironment';
import OperatingResources from './OperatingResources';
import Hires from './Hires';
import AdditionalCosts from './AdditionalCosts';
import ResultsDisplay from './ResultsDisplay';
import ConfigurationIO from './ConfigurationIO';
import DatabaseConfigIO from './DatabaseConfigIO';
import useLocalization from '../hooks/useLocalization';
import {
    calculateEffectiveEquity,
    calculateEffectiveEquityForPeriods,
    calculateEffectiveSalary,
    calculateEffectiveSalaryForPeriods,
    calculateCultureScore,
    calculateCapitalizationScore,
    getCultureLevel,
    getCapitalizationLevel,
    formatCurrency as formatCurrencyUtil,
    applyLocationMultiplier as applyLocationMultiplierUtil
} from '../utils/calculations';

const StartupCalculator = ({ isAuthenticated }) => {
    // Get translations from our hook
    const translations = useLocalization();

    // Base startup configuration
    const [baseSuccessRate, setBaseSuccessRate] = useState(10);
    const [location, setLocation] = useState('stcharles');

    // Core team salaries
    const [tristenSalaryTier, setTristenSalaryTier] = useState(1);
    const [martinSalaryTier, setMartinSalaryTier] = useState(1);

    // Hires
    const [hireDesigner, setHireDesigner] = useState(false);
    const [designerSalary, setDesignerSalary] = useState(70000);
    const [designerEquity, setDesignerEquity] = useState(1.0);

    // EA options
    const [eaType, setEaType] = useState('none'); // 'none', 'regular', 'clinical'
    const [eaSalary, setEaSalary] = useState(30000);
    const [eaEquity, setEaEquity] = useState(0.125);

    // Social Media Manager
    const [hireSocial, setHireSocial] = useState(false);
    const [socialSalary, setSocialSalary] = useState(30000);
    const [socialEquity, setSocialEquity] = useState(0.375);

    const [tristenFinalSalary, setTristenFinalSalary] = useState(0);
    const [martinFinalSalary, setMartinFinalSalary] = useState(0);

    const [designerHoursPerWeek, setDesignerHoursPerWeek] = useState(40);
    const [designerEmploymentType, setDesignerEmploymentType] = useState('employee');
    const [designerHireStartMonth, setDesignerHireStartMonth] = useState(0);

    // EA options
    const [eaHoursPerWeek, setEaHoursPerWeek] = useState(40);
    const [eaEmploymentType, setEaEmploymentType] = useState('employee');
    const [eaHireStartMonth, setEaHireStartMonth] = useState(0);

    // Social Media Manager options
    const [socialHoursPerWeek, setSocialHoursPerWeek] = useState(40);
    const [socialEmploymentType, setSocialEmploymentType] = useState('employee');
    const [socialHireStartMonth, setSocialHireStartMonth] = useState(0);

    // Work Environment
    const [workEnvironment, setWorkEnvironment] = useState('garage');

    // Operating Resources
    const [operatingResources, setOperatingResources] = useState('sustainable');

    // Additional costs
    const [legalCosts, setLegalCosts] = useState(0);
    const [travelCosts, setTravelCosts] = useState(0);
    const [additionalCosts, setAdditionalCosts] = useState(0);

    const [designerPeriods, setDesignerPeriods] = useState([{
        id: 'designer-default',
        startMonth: 0,
        endMonth: 12,
        hoursPerWeek: designerHoursPerWeek,
        employmentType: designerEmploymentType
    }]);

    // EA periods
    const [eaPeriods, setEaPeriods] = useState([{
        id: 'ea-default',
        startMonth: 0,
        endMonth: 12,
        hoursPerWeek: eaHoursPerWeek,
        employmentType: eaEmploymentType
    }]);

    // Social Media Manager periods
    const [socialPeriods, setSocialPeriods] = useState([{
        id: 'social-default',
        startMonth: 0,
        endMonth: 12,
        hoursPerWeek: socialHoursPerWeek,
        employmentType: socialEmploymentType
    }]);

    // Calculated values
    const [totalSalaries, setTotalSalaries] = useState(0);
    const [totalOperatingCosts, setTotalOperatingCosts] = useState(0);
    const [totalEnvironmentCosts, setTotalEnvironmentCosts] = useState(0);
    const [totalAdditionalCosts, setTotalAdditionalCosts] = useState(0);
    const [totalCosts, setTotalCosts] = useState(0);
    const [successRateModifier, setSuccessRateModifier] = useState(0);
    const [finalSuccessRate, setFinalSuccessRate] = useState({
        base: 10,
        min: 10,
        max: 10
    });

    const [locationMultiplier, setLocationMultiplier] = useState(1.64);
    
    // Use localized data from our translations
    const { baseConfig, locationMultiplier: locationMultiplierInfo, salaryTiers, 
            salarySelector: salarySelectorConfig, coreTeam: coreTeamInfo, environment: environmentInfo, 
            operatingResources: operatingResourcesInfo, hires: hiresConfig, 
            hireOptions: hireOptionsConfig, hireConfigPeriod: hireConfigPeriodInfo, 
            multiPeriodConfig: multiPeriodConfigInfo, hireCostSummary: hireCostSummaryInfo, 
            additionalCosts: additionalCostsInfo, summaryBox: summaryBoxInfo, 
            results: resultsInfo, loginScreen: loginScreenInfo, 
            stCharlesWorkEnvironments, operatingResourcesOptions } = translations;

    // Handle applying a bulk configuration update
    const handleConfigApply = (configUpdates) => {
        // Apply each update to the corresponding state
        for (const [key, value] of Object.entries(configUpdates)) {
            // Find the appropriate setter function for this key
            const setterName = `set${key.charAt(0).toUpperCase() + key.slice(1)}`;
            const setter = eval(setterName); // Use the setter function from component scope

            if (typeof setter === 'function') {
                setter(value);
            } else {
                console.warn(`No setter found for configuration property: ${key}`);
            }
        }
    };

    // Get the appropriate work environments based on location
    const getWorkEnvironments = () => {
        // Use the stCharlesWorkEnvironments from our translations
        // We need to dynamically calculate the San Francisco environments though
        if (location === 'sanfrancisco') {
            // Create SF environments with dynamic location multiplier applied
            return [
                {
                    name: 'coworking',
                    cost: stCharlesWorkEnvironments[1].cost * locationMultiplier,
                    modifier: stCharlesWorkEnvironments[1].modifier,
                    description: `Co-working space: $${Math.round(stCharlesWorkEnvironments[1].cost * locationMultiplier).toLocaleString()}/year`,
                    detailedDescription: 'Comparable co-working space in San Francisco with similar amenities to the St. Charles option.'
                },
                {
                    name: 'office',
                    cost: stCharlesWorkEnvironments[2].cost * locationMultiplier,
                    modifier: stCharlesWorkEnvironments[2].modifier,
                    description: `Private Office: $${Math.round(stCharlesWorkEnvironments[2].cost * locationMultiplier).toLocaleString()}/year`,
                    detailedDescription: 'Comparable private office space in San Francisco with similar features to the St. Charles option.'
                }
            ];
        }
        return stCharlesWorkEnvironments;
    };

    // Update work environment when location changes
    useEffect(() => {
        const environments = getWorkEnvironments();
        // If current work environment is not available in the new location (like garage in SF)
        if (!environments.find(env => env.name === workEnvironment)) {
            // Set to the first available option for the new location
            setWorkEnvironment(environments[0].name);
        }
    }, [location]);

    useEffect(() => {
        // Update designer periods with current hours and employment type
        setDesignerPeriods([{
            id: 'designer-default',
            startMonth: 0,
            endMonth: 12,
            hoursPerWeek: designerHoursPerWeek,
            employmentType: designerEmploymentType
        }]);

        // Update EA periods with current hours and employment type
        setEaPeriods([{
            id: 'ea-default',
            startMonth: 0,
            endMonth: 12,
            hoursPerWeek: eaHoursPerWeek,
            employmentType: eaEmploymentType
        }]);

        // Update social periods with current hours and employment type
        setSocialPeriods([{
            id: 'social-default',
            startMonth: 0,
            endMonth: 12,
            hoursPerWeek: socialHoursPerWeek,
            employmentType: socialEmploymentType
        }]);
    }, []);

    // Calculate designer equity from salary
    useEffect(() => {
        if (hireDesigner) {
            if (designerEmploymentType === 'contractor') {
                // No equity for contractors
                setDesignerEquity(0);
            } else {
                // Calculate based on salary and adjust for part-time
                const baseEquity = hiresConfig.designer.equityCalculation.formula(designerSalary);
                const adjustedEquity = calculateEffectiveEquityForPeriods(baseEquity, designerPeriods, designerEmploymentType);
                setDesignerEquity(parseFloat(adjustedEquity.toFixed(2)));
            }
        }
    }, [hireDesigner, designerSalary, designerHoursPerWeek, designerEmploymentType]);

    // Calculate EA equity from salary
    useEffect(() => {
        if (eaType === 'regular') {
            if (eaEmploymentType === 'contractor') {
                // No equity for contractors
                setEaEquity(0);
            } else {
                // Calculate based on salary and adjust for part-time
                const baseEquity = hiresConfig.ea.types.regular.equityCalculation.formula(eaSalary);
                const adjustedEquity = calculateEffectiveEquityForPeriods(baseEquity, eaPeriods, eaEmploymentType);
                setEaEquity(parseFloat(adjustedEquity.toFixed(2)));
            }
        } else if (eaType === 'clinical') {
            if (eaEmploymentType === 'contractor') {
                // No equity for contractors
                setEaEquity(0);
            } else {
                // Calculate based on salary and adjust for part-time
                const baseEquity = hiresConfig.ea.types.clinical.equityCalculation.formula(eaSalary);
                const adjustedEquity = calculateEffectiveEquityForPeriods(baseEquity, eaPeriods, eaEmploymentType);
                setEaEquity(parseFloat(adjustedEquity.toFixed(2)));
            }
        }
    }, [eaType, eaSalary, eaHoursPerWeek, eaEmploymentType]);

    // Calculate social media manager equity from salary
    useEffect(() => {
        if (hireSocial) {
            if (socialEmploymentType === 'contractor') {
                // No equity for contractors
                setSocialEquity(0);
            } else {
                // Calculate based on salary and adjust for part-time
                const baseEquity = hiresConfig.social.equityCalculation.formula(socialSalary);
                const adjustedEquity = calculateEffectiveEquityForPeriods(baseEquity, socialPeriods, socialEmploymentType);
                setSocialEquity(parseFloat(adjustedEquity.toFixed(2)));
            }
        }
    }, [hireSocial, socialSalary, socialHoursPerWeek, socialEmploymentType]);

    // Calculate total costs and success rate
    useEffect(() => {
        // Core team salaries
        let tristenSalary = salaryTiers[tristenSalaryTier].amount;
        const martinSalary = salaryTiers[martinSalaryTier].amount;

        // Adjust Tristen's salary if EA is hired
        if (eaType !== 'none') {
            tristenSalary -= coreTeamInfo.eaSavingsAmount;
        }
        setTristenFinalSalary(tristenSalary);
        setMartinFinalSalary(martinSalary);
        
        // Calculate total salaries (with location multiplier applied)
        let totalSalary = applyLocationMultiplier(tristenSalary + martinSalary);

        // Designer costs
        if (hireDesigner) {
            const effectiveDesignerSalary = calculateEffectiveSalaryForPeriods(designerSalary, designerPeriods, designerEmploymentType);
            totalSalary += applyLocationMultiplier(effectiveDesignerSalary);
        }

        // Calculate EA costs
        if (eaType === 'regular' || eaType === 'clinical') {
            const effectiveEaSalary = calculateEffectiveSalaryForPeriods(eaSalary, eaPeriods, eaEmploymentType);
            totalSalary += applyLocationMultiplier(effectiveEaSalary);

            // Calculate EA savings impact on Tristen's salary
            let eaSavings = 0;
            eaPeriods.forEach(period => {
                const monthsInPeriod = period.endMonth - period.startMonth;
                if (monthsInPeriod <= 0) return;

                const yearFraction = monthsInPeriod / 12;
                const hoursFraction = period.hoursPerWeek / 40;

                // Scale savings by time and hours
                eaSavings += coreTeamInfo.eaSavingsAmount * hoursFraction * yearFraction;
            });

            // Adjust Tristen's savings
            tristenSalary -= eaSavings;
        }

        // Calculate social media manager costs
        if (hireSocial) {
            const effectiveSocialSalary = calculateEffectiveSalaryForPeriods(socialSalary, socialPeriods, socialEmploymentType);
            totalSalary += applyLocationMultiplier(effectiveSocialSalary);
        }

        setTotalSalaries(totalSalary);

        // Get current work environments based on location
        const workEnvironments = getWorkEnvironments();

        // Environment costs (with location multiplier applied)
        const envObject = workEnvironments.find(env => env.name === workEnvironment);
        const envCost = envObject ? applyLocationMultiplier(envObject.cost) : 0;
        setTotalEnvironmentCosts(envCost);

        // Operating resources (with location multiplier applied)
        const resObject = operatingResourcesOptions.find(res => res.name === operatingResources);
        const operatingCost = resObject ? applyLocationMultiplier(resObject.costPerMonth * 12) : 0;
        setTotalOperatingCosts(operatingCost);

        // Additional costs (don't apply multiplier to these)
        const additionalTotal = legalCosts + travelCosts + additionalCosts;
        setTotalAdditionalCosts(additionalTotal);

        // Total costs (now we don't need to apply the multiplier again)
        const total = totalSalary + envCost + operatingCost + additionalTotal;
        setTotalCosts(total);

        // Success rate calculation
        let modifier = 0;

        // Core team salary impact
        modifier += salaryTiers[tristenSalaryTier].modifier;
        modifier += salaryTiers[martinSalaryTier].modifier;

        // Work environment impact
        const workEnvObj = workEnvironments.find(env => env.name === workEnvironment);
        modifier += workEnvObj ? workEnvObj.modifier : 0;

        // Operating resources impact
        const opResObj = operatingResourcesOptions.find(res => res.name === operatingResources);
        modifier += opResObj ? opResObj.modifier : 0;

        // Calculate hire impacts - WITHOUT scaling by hours and timeline
        let hiresMaxImpact = 0;

        // Simply add the full impact if the hire is active
        if (hireDesigner) {
            // Use the full success impact regardless of hours or periods
            hiresMaxImpact += 5.0;
        }

        if (eaType === 'regular') {
            // Use the full success impact regardless of hours or periods
            hiresMaxImpact += 2.0;
        } else if (eaType === 'clinical') {
            // Use the full success impact regardless of hours or periods
            hiresMaxImpact += 7.0;
        }

        if (hireSocial) {
            // Use the full success impact regardless of hours or periods
            hiresMaxImpact += 1.0;
        }

        setSuccessRateModifier(modifier);

        // Calculate culture and capitalization with current values
        const config = {
            tristenSalaryTier,
            martinSalaryTier,
            hireDesigner, 
            eaType,
            hireSocial,
            workEnvironment, 
            operatingResources,
            location,
            totalCosts: total,
            designerEmploymentType,
            designerEquity,
            eaEmploymentType,
            eaEquity,
            socialEmploymentType,
            socialEquity
        };
        
        const culture = calculateCultureScore(config);
        const capitalScore = calculateCapitalizationScore(config);

        setCultureScore(culture);
        setCapitalizationScore(capitalScore);

        // Final success rate with range
        const baseSuccess = baseSuccessRate + modifier;
        setFinalSuccessRate({
            base: parseFloat(baseSuccess.toFixed(1)),
            min: parseFloat(baseSuccess.toFixed(1)),
            max: parseFloat((baseSuccess + hiresMaxImpact).toFixed(1))
        });

    }, [
        baseSuccessRate, location, tristenSalaryTier, martinSalaryTier,
        hireDesigner, designerSalary, designerPeriods, designerEmploymentType,
        eaType, eaSalary, eaPeriods, eaEmploymentType,
        hireSocial, socialSalary, socialPeriods, socialEmploymentType,
        workEnvironment, operatingResources,
        legalCosts, travelCosts, additionalCosts
    ]);

    // Login is now handled by the App component - this is a dummy function
    // that's no longer needed but kept to avoid needing to change all the references
    const handleLogin = () => {};

    // Use our utility function but with defaults for location
    const formatCurrency = (amount, showLocationIndicator = false) => {
        return formatCurrencyUtil(amount, showLocationIndicator, location);
    };

    // Use our utility function but with defaults for location
    const applyLocationMultiplier = (amount) => {
        return applyLocationMultiplierUtil(amount, location, locationMultiplier);
    };

    // Update any localized strings that need formatCurrency
    const dynamicSalaryConfig = {
        ...salarySelectorConfig,
        eaAdjustmentText: `When an Executive Assistant is hired, Tristen's salary is reduced by ${formatCurrency(20000)} due to money saved on food delivery and errands.`
    };

    // Calculate salary tier descriptions for Tristen (with EA adjustment)
    const getTristenSalaryDescription = () => {
        const tier = salaryTiers[tristenSalaryTier];
        if (eaType !== 'none') {
            const adjustedAmount = tier.amount - coreTeamInfo.eaSavingsAmount;
            return `${tier.description.replace(tier.amount, formatCurrency(adjustedAmount))} (EA discount applied)`;
        }
        return tier.description;
    };

    // Add these state variables
    const [cultureScore, setCultureScore] = useState(0);
    const [capitalizationScore, setCapitalizationScore] = useState(0);

    // Authentication is now handled at the App level

    return (
        <div className="min-h-screen bg-gray-100 py-6 px-4">
            <div className="max-w-6xl mx-auto">
                <div className="bg-white rounded-lg shadow-md p-6 mb-6">
                    <h1 className="text-3xl font-bold mb-2 text-center">Startup Success Calculator</h1>
                    <p className="text-center text-gray-600 mb-6">Estimate your startup's probability of success based
                        on various factors</p>

                    <ConfigurationIO
                        state={{
                            baseSuccessRate,
                            location,
                            tristenSalaryTier,
                            martinSalaryTier,
                            hireDesigner,
                            designerSalary,
                            designerHoursPerWeek,
                            designerEmploymentType,
                            designerPeriods,
                            eaType,
                            eaSalary,
                            eaHoursPerWeek,
                            eaEmploymentType,
                            eaPeriods,
                            hireSocial,
                            socialSalary,
                            socialHoursPerWeek,
                            socialEmploymentType,
                            socialPeriods,
                            workEnvironment,
                            operatingResources,
                            legalCosts,
                            travelCosts,
                            additionalCosts
                        }}
                        onConfigApply={handleConfigApply}
                    />

                    {/* Only show database configuration when authenticated */}
                    {isAuthenticated && (
                        <DatabaseConfigIO
                            state={{
                                baseSuccessRate,
                                location,
                                tristenSalaryTier,
                                martinSalaryTier,
                                hireDesigner,
                                designerSalary,
                                designerHoursPerWeek,
                                designerEmploymentType,
                                designerPeriods,
                                eaType,
                                eaSalary,
                                eaHoursPerWeek,
                                eaEmploymentType,
                                eaPeriods,
                                hireSocial,
                                socialSalary,
                                socialHoursPerWeek,
                                socialEmploymentType,
                                socialPeriods,
                                workEnvironment,
                                operatingResources,
                                legalCosts,
                                travelCosts,
                                additionalCosts
                            }}
                            onConfigApply={handleConfigApply}
                        />
                    )}

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8">
                        {/* Left column */}
                        <div>
                            <BaseConfiguration
                                baseSuccessRate={baseSuccessRate}
                                setBaseSuccessRate={setBaseSuccessRate}
                                location={location}
                                setLocation={setLocation}
                                baseConfigInfo={baseConfig}
                                locationMultiplier={locationMultiplier}
                                setLocationMultiplier={setLocationMultiplier}
                                locationMultiplierInfo={locationMultiplierInfo}
                            />


                            <CoreTeamSalaries
                                tristenSalaryTier={tristenSalaryTier}
                                setTristenSalaryTier={setTristenSalaryTier}
                                martinSalaryTier={martinSalaryTier}
                                setMartinSalaryTier={setMartinSalaryTier}
                                salaryTiers={salaryTiers}
                                getTristenSalaryDescription={getTristenSalaryDescription}
                                eaType={eaType}
                                formatCurrency={formatCurrency}
                                applyLocationMultiplier={applyLocationMultiplier}
                                location={location}
                                coreTeamInfo={coreTeamInfo}
                                salarySelectorConfig={dynamicSalaryConfig}
                                summaryBoxInfo={summaryBoxInfo}
                            />

                            <WorkEnvironment
                                workEnvironment={workEnvironment}
                                setWorkEnvironment={setWorkEnvironment}
                                workEnvironments={getWorkEnvironments()}
                                environmentInfo={environmentInfo}
                                formatCurrency={formatCurrency}
                                applyLocationMultiplier={applyLocationMultiplier}
                                location={location}
                                summaryBoxInfo={summaryBoxInfo}
                            />

                            <OperatingResources
                                operatingResources={operatingResources}
                                setOperatingResources={setOperatingResources}
                                operatingResourcesOptions={operatingResourcesOptions}
                                operatingResourcesInfo={operatingResourcesInfo}
                                formatCurrency={formatCurrency}
                                applyLocationMultiplier={applyLocationMultiplier}
                                location={location}
                                summaryBoxInfo={summaryBoxInfo}
                            />
                        </div>

                        {/* Right column */}
                        <div>
                            <Hires
                                hireDesigner={hireDesigner}
                                setHireDesigner={setHireDesigner}
                                designerSalary={designerSalary}
                                setDesignerSalary={setDesignerSalary}
                                designerEquity={designerEquity}
                                setDesignerEquity={setDesignerEquity}
                                designerPeriods={designerPeriods}
                                setDesignerPeriods={setDesignerPeriods}
                                designerEmploymentType={designerEmploymentType}
                                setDesignerEmploymentType={setDesignerEmploymentType}

                                eaType={eaType}
                                setEaType={setEaType}
                                eaSalary={eaSalary}
                                setEaSalary={setEaSalary}
                                eaEquity={eaEquity}
                                setEaEquity={setEaEquity}
                                eaPeriods={eaPeriods}
                                setEaPeriods={setEaPeriods}
                                eaEmploymentType={eaEmploymentType}
                                setEaEmploymentType={setEaEmploymentType}

                                hireSocial={hireSocial}
                                setHireSocial={setHireSocial}
                                socialSalary={socialSalary}
                                setSocialSalary={setSocialSalary}
                                socialEquity={socialEquity}
                                setSocialEquity={setSocialEquity}
                                socialPeriods={socialPeriods}
                                setSocialPeriods={setSocialPeriods}
                                socialEmploymentType={socialEmploymentType}
                                setSocialEmploymentType={setSocialEmploymentType}

                                formatCurrency={formatCurrency}
                                hiresConfig={hiresConfig}
                                hiresInfo={hiresConfig}
                                location={location}
                                applyLocationMultiplier={applyLocationMultiplier}

                                // Localization configs
                                hireConfigPeriodInfo={hireConfigPeriodInfo}
                                multiPeriodConfigInfo={multiPeriodConfigInfo}
                                hireCostSummaryInfo={hireCostSummaryInfo}
                                summaryBoxInfo={summaryBoxInfo}
                            />


                            <AdditionalCosts
                                legalCosts={legalCosts}
                                setLegalCosts={setLegalCosts}
                                travelCosts={travelCosts}
                                setTravelCosts={setTravelCosts}
                                additionalCosts={additionalCosts}
                                setAdditionalCosts={setAdditionalCosts}
                                additionalCostsInfo={additionalCostsInfo}
                            />
                        </div>
                    </div>

                    <ResultsDisplay
                        totalSalaries={totalSalaries}
                        totalEnvironmentCosts={totalEnvironmentCosts}
                        totalOperatingCosts={totalOperatingCosts}
                        totalAdditionalCosts={totalAdditionalCosts}
                        totalCosts={totalCosts}
                        location={location}
                        locationMultiplier={locationMultiplier}
                        baseSuccessRate={baseSuccessRate}
                        successRateModifier={successRateModifier}
                        finalSuccessRate={finalSuccessRate}
                        formatCurrency={formatCurrency}
                        resultsInfo={resultsInfo}

                        // Additional props for itemized display
                        tristenSalaryTier={tristenSalaryTier}
                        martinSalaryTier={martinSalaryTier}
                        salaryTiers={salaryTiers}
                        tristenFinalSalary={tristenFinalSalary}
                        martinFinalSalary={martinFinalSalary}

                        // Hire details
                        hireDesigner={hireDesigner}
                        designerSalary={designerSalary}
                        designerEquity={designerEquity}
                        designerPeriods={designerPeriods}
                        designerEmploymentType={designerEmploymentType}

                        eaType={eaType}
                        eaSalary={eaSalary}
                        eaEquity={eaEquity}
                        eaPeriods={eaPeriods}
                        eaEmploymentType={eaEmploymentType}

                        hireSocial={hireSocial}
                        socialSalary={socialSalary}
                        socialEquity={socialEquity}
                        socialPeriods={socialPeriods}
                        socialEmploymentType={socialEmploymentType}

                        // Work environment
                        workEnvironment={workEnvironment}
                        workEnvironments={getWorkEnvironments()}

                        // Operating resources
                        operatingResources={operatingResources}
                        operatingResourcesOptions={operatingResourcesOptions}

                        // Additional costs
                        legalCosts={legalCosts}
                        travelCosts={travelCosts}
                        additionalCosts={additionalCosts}

                        // Helper functions
                        applyLocationMultiplier={applyLocationMultiplier}
                        calculateEffectiveSalaryForPeriods={calculateEffectiveSalaryForPeriods}

                        cultureScore={cultureScore}
                        capitalizationScore={capitalizationScore}
                        getCapitalizationLevel={getCapitalizationLevel}
                        getCultureLevel={getCultureLevel}
                        hiresConfig={hiresConfig}
                    />
                </div>
            </div>
        </div>
    );
};

export default StartupCalculator;